/*
 * ─── คลังกราฟิก (องค์ประกอบ): the rules — no DOM, no Fabric, so the tests load this ────────────────────────────────
 * Choices (25 ต.ค. 2569): round 1 = ก คลังของกลุ่ม + ชุดฟรี (Microsoft Fluent Emoji, Phosphor Icons — both MIT, ข every
 * style) · ก เปลี่ยนสีได้ + คมทุกขนาด (a vector is put into the work as shapes) · ก ผมแปลคำค้นไทยให้ครบทุกชิ้น
 * (src/graphics/th/*.json, checked by scripts/free-graphics/build-catalog.mjs) · round 2 Pixabay · round 3 AI.
 */

/** 'shape': a shape of องค์ประกอบ → รูปทรง (src/editor/shapeLib.ts), found by the same search */
export type GfxSource = 'group' | 'fluent' | 'phosphor' | 'shape';

/** one thing in the panel: a graphic of the group's library or of a free set */
export interface GfxItem {
  src: GfxSource;
  id: string;
  /** shown under the tile / read out: Thai first */
  title: string;
  /** a category (group) or the set's own group, for the chips */
  group: string;
  /** every word it can be found by, lower case */
  hay: string;
  /** the group's library: its file and type */
  file?: string;
  type?: string;
  hidden?: boolean;
  mine?: boolean;
}

/** the styles of each free set, as the app names them (the same ids as worker/rules.ts FREE_STYLES) */
export const FLUENT_STYLES = [
  { id: '3d', th: '3 มิติ' }, { id: 'color', th: 'สีสด' }, { id: 'flat', th: 'แบน' }, { id: 'hc', th: 'เส้นเดียว' }
] as const;
export const PHOSPHOR_STYLES = [
  { id: 'regular', th: 'ปกติ' }, { id: 'thin', th: 'บาง' }, { id: 'light', th: 'เบา' }, { id: 'bold', th: 'หนา' },
  { id: 'fill', th: 'ทึบ' }, { id: 'duotone', th: 'สองโทน' }
] as const;
export type FluentStyle = typeof FLUENT_STYLES[number]['id'];
export type PhosphorStyle = typeof PHOSPHOR_STYLES[number]['id'];

/** the colour a Phosphor icon starts in (the app's dark ink, as new lines) */
export const ICON_INK = '#1D1A2B';
/** a graphic is put on the page with its longer side this part of the page's shorter side */
export const PLACE_PART = 0.3;
/** an SVG that has to become a picture is drawn this big (px, its longer side) */
export const RASTER_SIDE = 1024;
/** tiles drawn at once; "แสดงเพิ่ม" draws the next ones */
export const PAGE_TILES = 60;

/** the search list (src/graphics/freeSearch.json) */
export interface FreeSearch {
  fluent: Array<[id: string, name: string, group: string, kw: string, th: string]>;
  phosphor: Array<[id: string, group: string, kw: string, th: string]>;
}

const lower = (s: string) => s.normalize('NFC').toLowerCase();

export function freeItems(s: FreeSearch): { fluent: GfxItem[]; phosphor: GfxItem[] } {
  return {
    fluent: s.fluent.map(([id, name, group, kw, th]) => ({ src: 'fluent', id, title: `${th.split(' ')[0]} · ${name}`, group, hay: lower(`${th} ${kw}`) })),
    phosphor: s.phosphor.map(([id, group, kw, th]) => ({ src: 'phosphor', id, title: `${th.split(' ')[0]} · ${id.replace(/-/g, ' ')}`, group, hay: lower(`${th} ${kw}`) }))
  };
}

export function groupItem(g: { id: string; name: string; keywords: string; categoryId: string; file: string; type: string; hidden: boolean; mine: boolean }): GfxItem {
  return { src: 'group', id: g.id, title: g.name, group: g.categoryId, hay: lower(`${g.name} ${g.keywords}`), file: g.file, type: g.type, hidden: g.hidden, mine: g.mine };
}

/** the words of a search (spaces, commas) — empty = show everything */
export const queryWords = (q: string) => lower(q).split(/[\s,]+/).filter(Boolean);

/**
 * The items every word of the search is found in, best first: a word that is a whole search word of the item
 * (Thai typed exactly) before one that only starts one, before one found inside a word; the list's own order otherwise.
 */
export function searchItems(items: GfxItem[], q: string): GfxItem[] {
  const words = queryWords(q);
  if (!words.length) return items;
  const scored: Array<{ it: GfxItem; score: number; at: number }> = [];
  items.forEach((it, at) => {
    const parts = it.hay.split(' ');
    let score = 0;
    for (const w of words) {
      if (!it.hay.includes(w)) return;
      score += parts.includes(w) ? 3 : parts.some(p => p.startsWith(w)) ? 2 : 1;
    }
    // the item's first Thai word (its main name) counts once more
    if (parts[0] === words[0]) score += 2;
    scored.push({ it, score, at });
  });
  return scored.sort((a, b) => b.score - a.score || a.at - b.at).map(s => s.it);
}

/** where an item's file is, in a style (the group's library has one file) */
export function fileOf(it: GfxItem, style: string): string {
  if (it.src === 'group' || it.src === 'shape') return it.file!;
  return `/api/free/${it.src}/${style}/${it.id}`;
}

/** how an item goes into the work: a vector (shapes, any colour, sharp at any size) or a picture */
export function kindOf(it: GfxItem, style: string): 'svg' | 'picture' {
  if (it.src === 'shape') return 'svg';
  if (it.src === 'group') return it.type === 'image/svg+xml' ? 'svg' : 'picture';
  if (it.src === 'fluent' && style === '3d') return 'picture';
  return 'svg';
}

/**
 * Whether an SVG can be put in as shapes, and the SVG to give Fabric: a clip that is only the whole drawing area (what
 * design programs add to every export) is taken out — it would make print draw the piece as a picture for nothing.
 * Effects Fabric cannot draw (filters, masks, other clips, gradients and patterns it would draw differently) make it a
 * picture instead, with the reason said.
 */
export function vectorPlan(svg: string): { vector: true; svg: string } | { vector: false; why: string } {
  if (/<filter[\s>]/i.test(svg)) return { vector: false, why: 'มีเอฟเฟกต์เงา/เบลอ' };
  if (/<mask[\s>]/i.test(svg)) return { vector: false, why: 'มีหน้ากากบังบางส่วน' };
  if (/<(linearGradient|radialGradient|pattern)[\s>]/i.test(svg)) return { vector: false, why: 'มีสีไล่ระดับ' };
  if (/<(text|image)[\s>]/i.test(svg)) return { vector: false, why: 'มีตัวหนังสือหรือรูปฝังอยู่' };
  let out = svg;
  const box = /<svg[^>]*\bviewBox\s*=\s*["']\s*([-\d.]+)[\s,]+([-\d.]+)[\s,]+([-\d.]+)[\s,]+([-\d.]+)\s*["']/i.exec(svg);
  for (const m of svg.matchAll(/<clipPath\b[^>]*\bid\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/clipPath>/gi)) {
    const r = /^\s*<rect\b([^>]*)\/>\s*$/i.exec(m[2]);
    const attr = (n: string) => Number(new RegExp(`\\b${n}\\s*=\\s*["']([-\\d.]+)["']`).exec(r?.[1] ?? '')?.[1] ?? (n === 'x' || n === 'y' ? 0 : NaN));
    const whole = !!r && !!box && !/transform/i.test(r[1])
      && attr('x') <= Number(box[1]) && attr('y') <= Number(box[2])
      && attr('x') + attr('width') >= Number(box[1]) + Number(box[3]) && attr('y') + attr('height') >= Number(box[2]) + Number(box[4]);
    if (!whole) return { vector: false, why: 'มีการครอบตัดรูปทรง' };
    const id = m[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(m[0], '').replace(new RegExp(`\\sclip-path\\s*=\\s*["']url\\(#${id}\\)["']`, 'g'), '');
  }
  if (/clip-path\s*=/i.test(out)) return { vector: false, why: 'มีการครอบตัดรูปทรง' };
  return { vector: true, svg: out };
}

/** a Phosphor icon (drawn in "currentColor") in one colour */
export const tintSvg = (svg: string, colour: string) => svg.replace(/currentColor/g, colour);

/** the size a graphic is put on the page at: its longer side PLACE_PART of the page's shorter side */
export function placeScale(w: number, h: number, pageW: number, pageH: number): number {
  const side = Math.max(w, h);
  return side > 0 ? Math.min(pageW, pageH) * PLACE_PART / side : 1;
}

/** the size an SVG is drawn at to become a picture: longer side RASTER_SIDE, its own proportions */
export function rasterSize(w: number, h: number): { w: number; h: number } {
  if (!(w > 0 && h > 0)) return { w: RASTER_SIDE, h: RASTER_SIDE };
  const k = RASTER_SIDE / Math.max(w, h);
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/**
 * The colours of a graphic, one slot per colour (the same colour on several shapes is one slot, as Canva does): which
 * shape and which of its paints each slot changes. Gradients are not slots (they keep their colours). `key` makes two
 * spellings of one colour the same (#FFF / white / rgb(255,255,255)) — the app passes Fabric's own reading of it.
 */
export interface Paint { fill?: unknown; stroke?: unknown; strokeWidth?: number }
export interface Slot<T> { colour: string; refs: Array<{ obj: T; prop: 'fill' | 'stroke' }> }
export function colourSlots<T extends Paint>(leaves: T[], key: (c: string) => string | null): Array<Slot<T>> {
  const slots = new Map<string, Slot<T>>();
  for (const obj of leaves) {
    for (const prop of ['fill', 'stroke'] as const) {
      const c = obj[prop];
      if (typeof c !== 'string' || !c || c === 'none' || c === 'transparent') continue;
      if (prop === 'stroke' && !((obj.strokeWidth ?? 1) > 0)) continue;
      const k = key(c);
      if (!k) continue;
      let s = slots.get(k);
      if (!s) { s = { colour: k, refs: [] }; slots.set(k, s); }
      s.refs.push({ obj, prop });
    }
  }
  return [...slots.values()];
}
